import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Puzzle,
  Download,
  Rocket,
  UserPlus,
  Gamepad,
  FolderOpen,
  Info,
} from "lucide-react";
import { ASSETS } from "@/assets";
import { Reveal } from "@/components/Reveal";
import { DISCORD_URL } from "@/constants/links";
import { JAK_HRAT_PAGE } from "@/constants/testIds";

const DownloadButton = ({ label, testId }) => {
  const [showNote, setShowNote] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setShowNote(true)}
        data-testid={testId}
        className="inline-flex items-center gap-2 rounded-md bg-[#F0801F]/25 px-6 py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-white/50 cursor-not-allowed"
      >
        <Download className="h-4 w-4" />
        {label}
      </button>
      {showNote && (
        <p className="mt-2.5 text-xs text-white/40">
          Projekt je stále ve vývoji - odkaz ke stažení doplníme před spuštěním serveru.
        </p>
      )}
    </div>
  );
};

export const JakHratPage = ({ onCreateAccount }) => {
  return (
    <section data-testid={JAK_HRAT_PAGE.section}>
      {/* Hero */}
      <div className="relative overflow-hidden px-6 pb-16 pt-40" data-testid={JAK_HRAT_PAGE.heroSection}>
        <img
          src={ASSETS.heroCastle}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-[65%_center] brightness-[0.85]"
        />
        <div className="absolute inset-0 bg-[#141417]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#141417]/70 to-[#141417]" />
        <div className="relative mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Jak hrát
            </p>
            <h1 className="mt-4 font-display text-5xl font-600 uppercase tracking-tight text-white sm:text-6xl">
              Od stažení po první krok
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
              Kompletní návod, jak se dostat z prázdného disku až do ulic Rowengardu.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Proč WoW */}
      <div className="px-6 py-16 lg:py-20" data-testid={JAK_HRAT_PAGE.forewordSection}>
        <div className="mx-auto grid max-w-[1584px] grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Než začneme
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Proč World of Warcraft?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/60">
              Jak už bylo řečeno, k hraní a online RolePlay používáme platformu hry World of
              Warcraft. Důvodů je mnoho - především skvělá moddovatelnost a spousta dostupných
              nástrojů pro vytváření custom obsahu, včetně světa i vlastních itemů. Má to
              jednoduchou strukturu databáze, takže nad obsahem máme prakticky plnou kontrolu.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/60">
              Z toho důvodu jsme byli schopni vytvořit svět Forgotten Realms právě na této
              platformě a můžeme si tak užít hraní v kulisách Lunomoří a Faerûnu.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex justify-center">
            <span className="grid h-40 w-40 place-items-center rounded-full border border-[#F0801F]/20 bg-[#17171b] text-[#F0801F] sm:h-52 sm:w-52">
              <Puzzle className="h-16 w-16 sm:h-20 sm:w-20" strokeWidth={1.2} />
            </span>
          </Reveal>
        </div>
      </div>

      {/* Kroky */}
      <div className="px-6 py-16 lg:py-20" data-testid={JAK_HRAT_PAGE.stepsSection}>
        <div className="mx-auto max-w-[1000px] space-y-6">
          {/* 1 - stáhnout hru */}
          <Reveal
            data-testid={JAK_HRAT_PAGE.stepCard}
            className="rounded-xl border border-white/10 bg-[#17171b] p-8"
          >
            <div className="flex items-start gap-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#F0801F]/40 font-display text-lg text-[#F0801F]">
                1
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-xl font-600 uppercase tracking-tight text-white">
                  Stáhni si hru
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  Co tedy budeš k hraní potřebovat? Budeš si potřebovat stáhnout naši upravenou
                  verzi hry.
                </p>
                <div className="mt-5">
                  <DownloadButton label="Stáhnout hru" testId={JAK_HRAT_PAGE.downloadClientButton} />
                </div>
              </div>
            </div>
          </Reveal>

          {/* 2 - launcher */}
          <Reveal
            delay={0.05}
            data-testid={JAK_HRAT_PAGE.stepCard}
            className="rounded-xl border border-white/10 bg-[#17171b] p-8"
          >
            <div className="flex items-start gap-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#F0801F]/40 font-display text-lg text-[#F0801F]">
                2
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-xl font-600 uppercase tracking-tight text-white">
                  Stáhni si Draconia Launcher
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  Měl by sis stáhnout náš launcher, který ti umožní hru spouštět a bude ji sám od
                  sebe udržovat aktuální, ať ti neuteče žádný patch ani novinka.
                </p>
                <ul className="mt-5 space-y-3">
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/15 text-white/50">
                      <FolderOpen className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    Launcher jednoduše vlož do složky s hrou (nebo kamkoliv jinam).
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/15 text-white/50">
                      <Rocket className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    Spusť aplikaci Draconia Launcher.
                  </li>
                  <li className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/15 text-white/50">
                      <Gamepad className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span>
                      Nasměruj ji do složky s hrou, například{" "}
                      <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-white/80">
                        D:\Games\World of Warcraft\
                      </code>
                      .
                    </span>
                  </li>
                </ul>
                <div className="mt-5">
                  <DownloadButton
                    label="Stáhnout launcher"
                    testId={JAK_HRAT_PAGE.downloadLauncherButton}
                  />
                </div>
              </div>
            </div>
          </Reveal>

          {/* 3 - účet */}
          <Reveal
            delay={0.1}
            data-testid={JAK_HRAT_PAGE.stepCard}
            className="rounded-xl border border-white/10 bg-[#17171b] p-8"
          >
            <div className="flex items-start gap-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#F0801F]/40 font-display text-lg text-[#F0801F]">
                3
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-xl font-600 uppercase tracking-tight text-white">
                  Vytvoř si účet
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  Jakmile budeš mít hotovo, nezbývá než si vytvořit účet přes naši registraci
                  zde na webu.
                </p>
                <button
                  type="button"
                  onClick={onCreateAccount}
                  data-testid={JAK_HRAT_PAGE.registerButton}
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#F0801F] px-6 py-3.5 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438]"
                >
                  <UserPlus className="h-4 w-4" />
                  Registrace
                </button>
              </div>
            </div>
          </Reveal>

          {/* 4 - hraj */}
          <Reveal
            delay={0.15}
            data-testid={JAK_HRAT_PAGE.stepCard}
            className="rounded-xl border border-white/10 bg-[#17171b] p-8"
          >
            <div className="flex items-start gap-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#F0801F]/40 font-display text-lg text-[#F0801F]">
                4
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-xl font-600 uppercase tracking-tight text-white">
                  Hru spouštěj zásadně přes launcher
                </h3>
                <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-white/60">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#F0801F]" strokeWidth={1.8} />
                  Launcher tě udrží na aktuální verzi hry i všech patchů - bez něj riskuješ, že
                  se nebudeš moct připojit.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* CTA na Discord */}
      <div className="px-6 pb-24" data-testid={JAK_HRAT_PAGE.ctaSection}>
        <Reveal className="mx-auto max-w-[1584px] rounded-xl border border-[#F0801F]/30 bg-[#17171b] px-6 py-14 text-center sm:px-10">
          <h2 className="font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
            Přidej se na Discord
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/60">
            Ať si s tebou můžeme pokecat! Těšíme se na tebe.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={JAK_HRAT_PAGE.discordButton}
              className="inline-flex items-center gap-2 rounded-md bg-[#F0801F] px-8 py-4 font-display text-sm font-600 uppercase tracking-[0.14em] text-[#141417] transition-colors hover:bg-[#ff9438]"
            >
              <i className="fa-brands fa-discord" />
              Discord
            </a>
            <Link
              to="/pravidla"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-8 py-4 font-display text-sm font-600 uppercase tracking-[0.14em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F]"
            >
              Přečíst pravidla
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
